import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axiosAdmin from '@/shared/api/instance/axiosAdmin';
import { usePartnerMenu } from '../pages/partner-admin/dashboard/hooks/usePartnerDashboard';
import { useAuth } from '../shared/hooks/useAuth';
import { useUser } from '../shared/store/userStore';

function Navigation() {
  const location = useLocation();
  const user = useUser();
  const { handleLogout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [navTabMenus, setNavTabMenus] = useState<any[]>([]);

  // 파트너사 사용자인지 확인
  const isPartnerUser = user?.organizationType === 'partner';

  // 백엔드에서 메뉴 정보 가져오기 (별도 API 호출)
  useEffect(() => {
    const fetchMenus = async () => {
      try {
        // 네비게이션 탭 메뉴 조회
        const navResponse = await axiosAdmin.get('/auth/navigation-tabs');

        // 네비게이션 탭 메뉴 처리
        if (navResponse.status === 200 && navResponse.data) {
          const navData = navResponse.data;
          setNavTabMenus(navData.data?.menuItems || []);
        }
      } catch (error) {
        console.error('[Navigation] Navigation 정보 조회 실패:', error);
      }
    };

    if (user) {
      fetchMenus();
    }
  }, [user, isPartnerUser]);

  // AdminLayout 내부인지 확인
  const isInAdminLayout = location.pathname.startsWith('/admin');

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  // 사용자 역할 표시 함수
  const getRoleDisplayName = () => {
    if (!user?.roles || user.roles.length === 0) return '사용자';

    const roleName = user.roles[0].name;
    if (
      roleName.includes('partner_manager') ||
      roleName.includes('PARTNER_MANAGER')
    ) {
      return '파트너사 매니저';
    } else if (
      roleName.includes('partner_employee') ||
      roleName.includes('PARTNER_EMPLOYEE')
    ) {
      return '파트너사 직원';
    } else if (roleName.includes('admin') || roleName.includes('ADMIN')) {
      return '관리자';
    } else if (roleName.includes('hr') || roleName.includes('HR')) {
      return 'HR';
    } else if (roleName.includes('marketer') || roleName.includes('MARKETER')) {
      return '마케터';
    } else if (
      roleName.includes('merchandiser') ||
      roleName.includes('MERCHANDISER')
    ) {
      return '머천다이저';
    }
    return roleName;
  };

  // 파트너 메뉴 조회 (파트너 사용자일 때만)
  const { data: partnerMenu } = usePartnerMenu(isPartnerUser);

  const linkClass = (path: string) => `
    relative px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap flex-shrink-0
    ${
      isActive(path)
        ? 'text-punta-orange border-b-[3px] border-punta-orange'
        : 'text-gray-700 hover:text-punta-orange hover:border-b-[3px] hover:border-gray-200'
    }
    group
  `;

  const mobileLinkClass = (path: string) => `
    block px-4 py-3 text-base font-medium transition-colors duration-200
    ${
      isActive(path)
        ? 'text-punta-orange bg-orange-50'
        : 'text-gray-700 hover:bg-gray-50'
    }
  `;

  return (
    <nav className='bg-white border-b border-gray-200 relative z-50'>
      {/* 데스크톱 네비게이션 */}
      <div className='hidden md:block'>
        <div className='container mx-auto px-4'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex space-x-6 overflow-x-auto flex-nowrap min-w-0 flex-1 mr-4'>
              {isPartnerUser ? (
                // 파트너사 전용 메뉴
                <>
                  <Link to='/home' className={linkClass('/home')}>
                    내 정보
                  </Link>
                  <Link
                    to='/admin/partner-dashboard'
                    className={linkClass('/admin/partner-dashboard')}
                  >
                    대시보드
                  </Link>
                  {partnerMenu?.menuItems?.map((menuItem: any) => (
                    <Link
                      key={menuItem.id}
                      to={menuItem.path}
                      className={linkClass(menuItem.path)}
                    >
                      {menuItem.title}
                    </Link>
                  ))}
                </>
              ) : (
                // 관리자 메뉴 (top navigation only shows essential tabs)
                <>
                  <Link to='/home' className={linkClass('/home')}>
                    내 정보
                  </Link>
                  <Link to='/admin' className={linkClass('/admin')}>
                    어드민
                  </Link>
                  {/* 네비게이션 탭 메뉴들만 표시 (adminMenus는 사이드바에서 처리) */}
                  {navTabMenus.map((menu) => (
                    <Link
                      key={menu.path}
                      to={menu.path}
                      className={linkClass(menu.path)}
                    >
                      {menu.displayName}
                    </Link>
                  ))}
                </>
              )}
            </div>

            {/* 우측 사용자 정보 영역 */}
            <div className='flex items-center space-x-4 flex-shrink-0'>
              {isPartnerUser && user && (
                <div className='px-4 py-2 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border border-orange-200 shadow-sm'>
                  <div className='text-right'>
                    <div className='text-sm font-semibold text-gray-900'>
                      {user.name}
                    </div>
                    <div className='text-xs text-orange-600 font-medium'>
                      {getRoleDisplayName()}
                    </div>
                    {user.partnerName && (
                      <div className='text-xs text-gray-500 mt-0.5'>
                        {user.partnerName}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <button
                type='button'
                onClick={handleLogout}
                className='text-gray-700 hover:text-punta-orange px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200'
              >
                로그아웃
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 모바일 네비게이션 - AdminLayout 내부에서는 숨김 */}
      {!isInAdminLayout && (
        <div className='md:hidden'>
          <div className='flex items-center justify-between px-4 h-16'>
            <button
              type='button'
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className='text-gray-700 hover:text-punta-orange'
            >
              <svg
                className='h-6 w-6'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 6h16M4 12h16M4 18h16'
                />
              </svg>
            </button>

            {/* 모바일 중앙 타이틀 */}
            <div className='flex-1 text-center'>
              {isPartnerUser && user ? (
                <div>
                  <h1 className='text-lg font-bold text-punta-orange'>
                    파트너사 어드민
                  </h1>
                  <div className='text-xs text-gray-600'>
                    {user.name} · {getRoleDisplayName()}
                  </div>
                  {user.partnerName && (
                    <div className='text-xs text-gray-500'>
                      {user.partnerName}
                    </div>
                  )}
                </div>
              ) : (
                <h1 className='text-lg font-bold text-punta-orange'>
                  식후경 어드민
                </h1>
              )}
            </div>

            <div className='flex items-center space-x-2'>
              <button
                type='button'
                onClick={handleLogout}
                className='text-gray-700 hover:text-punta-orange'
              >
                로그아웃
              </button>
            </div>
          </div>

          {/* 모바일 메뉴 */}
          <div
            className={`
              fixed inset-0 bg-gray-800 bg-opacity-75 z-50
              transition-opacity duration-300 ease-in-out
              ${
                isMobileMenuOpen
                  ? 'opacity-100'
                  : 'opacity-0 pointer-events-none'
              }
            `}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <div
              className={`
                fixed inset-y-0 left-0 max-w-xs w-full bg-white shadow-xl
                transform transition-transform duration-300 ease-in-out
                ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
              `}
              onClick={(e) => e.stopPropagation()}
            >
              <div className='flex items-center justify-between p-4 border-b'>
                <h2 className='text-xl font-bold text-punta-orange'>메뉴</h2>
                <button
                  type='button'
                  onClick={() => setIsMobileMenuOpen(false)}
                  className='text-gray-500 hover:text-gray-700'
                >
                  <svg
                    className='h-6 w-6'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
              <div className='py-2'>
                {isPartnerUser ? (
                  // 파트너사 전용 모바일 메뉴
                  <>
                    <Link
                      to='/home'
                      className={mobileLinkClass('/home')}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      내 정보
                    </Link>
                    <Link
                      to='/admin/partner-dashboard'
                      className={mobileLinkClass('/admin/partner-dashboard')}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      대시보드
                    </Link>
                    {partnerMenu?.menuItems?.map((menuItem: any) => (
                      <Link
                        key={menuItem.id}
                        to={menuItem.path}
                        className={mobileLinkClass(menuItem.path)}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {menuItem.title}
                      </Link>
                    ))}
                  </>
                ) : (
                  // 관리자 모바일 메뉴
                  <>
                    <Link
                      to='/home'
                      className={mobileLinkClass('/home')}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      내 정보
                    </Link>
                    <Link
                      to='/admin'
                      className={mobileLinkClass('/admin')}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      어드민
                    </Link>
                    {/* 네비게이션 탭 메뉴들만 표시 (adminMenus는 사이드바에서 처리) */}
                    {navTabMenus.map((menu) => (
                      <Link
                        key={menu.path}
                        to={menu.path}
                        className={mobileLinkClass(menu.path)}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {menu.displayName}
                      </Link>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navigation;
